import moment from 'moment';
import classnames from 'classnames';
import { Button } from '@wg/wows-react-uikit';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import styles from './StartBooster.scss';

import type Api from '~/types';
import type { RootState } from '~/store';

import { ACTION_IDS, DWH_EXPORT_EVENTS } from '~/constants';
import dwhExport from '~/dwhExport';

import { openStartBuyDialog } from '~/store/dialogs';

import { useAppDispatch, useAppSelector } from '~/hooks';
import { getStartBoostersIds, isTooLateToBuyStartBooster } from '~/utils/boosters';
import { LastChanceStartBoosterMessage, StartBoosterMessage } from '~/Components/Messages/Messages';


interface StartBoosterState {
    isLastChanceScreenVisible: boolean,
    popupWasShown: boolean,
    actionId?: string | null,
    appIsReady: boolean,
    isPending: boolean,
    isTransactionPending: boolean,
    isVisible: boolean,
    isAnyStartBoosterBought: boolean,
    banner: Api.Banner | null,
    levelsCompletedBySpendingDoubloons: number,
    shipId: string,
}

const stateSelector = (state: RootState): StartBoosterState => {
    const startterKitAction = state.app.actions.filter((a) => a.name === ACTION_IDS.STARTERKIT_TOOLTIP)[0];
    const startterKitActionId = startterKitAction ? startterKitAction.name : undefined;
    const completedActions: Array<Api.Action> = state.app.account.completedActions || [];
    const popupWasShown = completedActions.map((a) => a.name).includes(ACTION_IDS.STARTERKIT_TOOLTIP);
    const pendingTransactions: boolean = get(state.app, 'account.pendingTransactions', false);
    const startBoostersIds = getStartBoostersIds(state.app.boosters);
    const isAnyStartBoosterBought = state.app.account.boughtBoostersIds?.some((boughtBoosterId) => {
        return startBoostersIds.includes(boughtBoosterId);
    }) || false;
    const isLastChanceScreenVisible = !isTooLateToBuyStartBooster(state)
        && (state.app.account.boughtBoostersIds?.some((id) => !startBoostersIds.includes(id)) || false);
    const isTransactionPending = pendingTransactions;
    const isStartBoosterBought = state.app.account && startBoostersIds.every((id) => {
        return state.app.account.boughtBoostersIds?.includes(id);
    });
    const isProgressProgresSmall = !isTooLateToBuyStartBooster(state);
    const appIsReady = state.app.appIsReady && state.app.browserIsReady;
    const now = moment.utc().unix() * 1000;
    const isEventFinished = now >= state.app.closeTime && appIsReady;
    const isStartBoosterVisible = !isStartBoosterBought && isProgressProgresSmall && !isEventFinished;

    return {
        isLastChanceScreenVisible,
        popupWasShown,
        actionId: startterKitActionId,
        appIsReady: state.app.appIsReady && state.app.browserIsReady,
        isPending: state.app.isPendingBuyBooster,
        isTransactionPending,
        isVisible: isStartBoosterVisible,
        isAnyStartBoosterBought,
        banner: state.app.banner,
        levelsCompletedBySpendingDoubloons: state.app.levelsCompletedBySpendingDoubloons,
        shipId: state.app.mainRewardShipId,
    };
};

const StartBooster = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(stateSelector, equal);
    const { t } = useTranslation();
    const [isStartBoosterVisible, setIsStartBoosterVisible] = useState(false);

    const {
        isLastChanceScreenVisible,
        popupWasShown,
        appIsReady,
        isPending,
        isTransactionPending,
        isVisible,
        isAnyStartBoosterBought,
        banner,
    } = state;

    useEffect(() => {
        if (appIsReady && isVisible) {
            setTimeout(() => {
                setIsStartBoosterVisible(true);
            }, 500);
        }
        if (!isVisible) {
            setIsStartBoosterVisible(false);
        }
    }, [appIsReady, isVisible, isStartBoosterVisible, popupWasShown]);

    const onBuy = useCallback(() => {
        if (!isPending && !isTransactionPending) {
            playButtonClickSound();
            dispatch(openStartBuyDialog());
            dwhExport.send(DWH_EXPORT_EVENTS.START_BOOSTER_CLICK);
        }
    }, [isPending, isTransactionPending, dispatch]);

    const wrapper = classnames(styles.wrapper, {
        [styles.wrapper]: true,
        [styles.wrapperBought]: isAnyStartBoosterBought,
    });

    const wrapperStyles = {
        backgroundImage: banner?.imageUrl ? `url(${banner.imageUrl})` : '',
    };

    let title = banner?.title;
    if (!title) {
        title = isAnyStartBoosterBought ? t('Дополнительные этапы') : t('Стартовые наборы');
    }

    let subTitle = banner?.subTitle;
    if (!subTitle) {
        subTitle = isAnyStartBoosterBought
            ? t('Еще больше этапов строительства со скидкой.')
            : t('Позволяют завершить этапы строительства за дублоны.');
    }

    const buttonTitle = banner?.buttonTitle || t('Подробнее');

    return (
        <TransitionGroup className={styles.groupWrapper}>
            {isStartBoosterVisible ? (
                <CSSTransition key={'start-booster'} classNames={'start-booster-popup'} timeout={2500}>
                    <div className={wrapper} onClick={onBuy} style={wrapperStyles}>
                        <div className={styles.title}>{title}</div>
                        <div className={styles.subTitle}>{subTitle}</div>
                        <div className={styles.topMessage}>
                            { isLastChanceScreenVisible
                                ? <LastChanceStartBoosterMessage />
                                : <StartBoosterMessage />
                            }
                        </div>
                        <div className={styles.inline}>
                            <div className={styles.button}>
                                <Button
                                    isFlat={true}
                                    isLarger={true}
                                    type={'orange'}
                                    onClick={onBuy}
                                    isDisabled={isPending || isTransactionPending}
                                >
                                    {buttonTitle}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CSSTransition>
            ) : null}
        </TransitionGroup>
    );
};

export default StartBooster;
